import fromUnixTime from 'date-fns/fromUnixTime';
import isBefore from 'date-fns/isBefore';
import type { MintSchedule } from '@soundxyz/sdk';
import type { Maybe } from '@soundxyz/utils';
import { getMaxSupply, hasSaleEnded } from '@components/Song/MintComponents/helpers';
import { useAuth } from '@context/auth/base';
import type { ContractType, FragmentType, MintDataContractsFragmentDoc } from '@graphql/reactQuery';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { isOpenEdition } from '../../shared-helpers';
import { useGetRegularMintSchedules } from './useGetRegularMintSchedules';

export type PresaleDetails = {
  startTime: number;
  endTime: number;
  totalMinted: number;
  saleOngoing: boolean;
  saleStarted: boolean;
  hasAccess: boolean;
  canMintPresale: boolean;
  saleEnded: boolean;
  isSoldOut: boolean;
  formattedPrice: string;
  merkleRoot: string;
  maxSupply: number;
  maxedOutPerWallet: boolean;
} | null;

export type PublicSaleDetails = {
  startTime: number;
  endTime: number;
  totalMinted: number;
  saleOngoing: boolean;
  saleStarted: boolean;
  canMintPublicSale: boolean;
  saleEnded: boolean;
  isSoldOut: boolean;
  formattedPrice: string;
  maxMintableLower: number;
  maxSupply: number;
};

export const useMintState = ({
  release,
  chainId,
  editionAddress,
  auctionContractType,
  isFirstPhaseCompleted,
  rootTotalMinted,
  finalQuantity,
}: {
  release: FragmentType<MintDataContractsFragmentDoc>;
  chainId: number;
  editionAddress: string;
  rootTotalMinted: number;
  auctionContractType: ContractType | null;
  isFirstPhaseCompleted: boolean;
  finalQuantity: number | null;
}) => {
  const { isAuthenticatedWithAPI } = useAuth();

  const {
    presale,
    publicSale,
    hasPresaleAccess,
    presaleMaxedOutPerWallet,
    presaleSoldOut,
    publicSaleSoldOut,
    canMintPresale,
    canMintPublicSale,
    loading,
  } = useGetRegularMintSchedules({
    release,
    chainId,
    editionAddress,
    auctionContractType,
    rootTotalMinted,
    isFirstPhaseCompleted,
    finalQuantity,
  });

  const hasPresale = !!presale;

  const hasPresaleStarted = hasPresale
    ? hasSaleStarted({
        startTimestamp: presale.startTime,
      })
    : false;

  const hasPresaleEnded = hasPresale
    ? hasSaleEnded({
        schedule: presale,
        collected: presale.totalMinted,
      })
    : false;

  const hasOngoingPresale = hasPresaleStarted && !hasPresaleEnded;

  const presaleMaxSupply = presale ? getMaxSupply(presale) : 0;
  const publicMaxSupply = publicSale ? getMaxSupply(publicSale) : 0;

  const hasPublicSaleStarted = !!publicSale
    ? hasSaleStarted({
        startTimestamp: publicSale.startTime,
      })
    : false;

  const publicSaleMinted = rootTotalMinted;

  const hasPublicSaleEnded = !!publicSale
    ? hasSaleEnded({
        schedule: publicSale,
        collected: publicSaleMinted,
      })
    : false;

  const now = new Date();
  const publicSaleCutoff = publicSale ? fromUnixTime(publicSale.cutoffTime).getTime() : 0;
  const rangeExpiryCheck = now.getTime() > publicSaleCutoff;

  const hasOnGoingPublicSale = hasPublicSaleStarted && !hasPublicSaleEnded;
  const hasOnGoingSale = hasOngoingPresale || hasOnGoingPublicSale;
  const bothSalesOngoing = hasOngoingPresale && hasOnGoingPublicSale;

  const formattedPresalePrice = presale?.price.toString()
    ? formatWeiToEthDisplayFree({
        weiValue: presale?.price.toString(),
        maxDecimals: 4,
      })
    : null;

  const formattedPublicSalePrice = publicSale?.price.toString()
    ? formatWeiToEthDisplayFree({
        weiValue: publicSale?.price.toString(),
        maxDecimals: 4,
      })
    : null;

  const OEMinNotHit = publicSaleMinted < (publicSale?.maxMintableLower ?? 0);

  const isOpenEdition = isCurrentSaleOpenEdition({
    presaleMaxSupply,
    publicMaxSupply,
    hasOngoingPresale,
    hasPresaleAccess,
    isAuthenticatedWithAPI,
    publicSaleCutOff: rangeExpiryCheck,
    minNotHit: OEMinNotHit,
  });

  const isFixedEdition = isSaleFixedEdition({
    saleSchedule: publicSale,
    publicSaleCutOff: rangeExpiryCheck,
    minNotHit: publicSaleMinted < (publicSale?.maxMintableLower ?? 0),
  });

  const presaleDetails: PresaleDetails = hasPresale
    ? {
        startTime: presale.startTime,
        endTime: presale.endTime,
        totalMinted: presale.totalMinted,
        saleOngoing: hasOngoingPresale,
        saleStarted: hasPresaleStarted,
        hasAccess: hasPresaleAccess,
        canMintPresale,
        saleEnded: hasPresaleEnded,
        isSoldOut: presaleSoldOut,
        formattedPrice: formattedPresalePrice ?? '',
        merkleRoot: presale.merkleRoot,
        maxSupply: presaleMaxSupply,
        maxedOutPerWallet: presaleMaxedOutPerWallet,
      }
    : null;

  const publicSaleDetails: PublicSaleDetails = {
    startTime: publicSale?.startTime ?? 0,
    endTime: publicSale?.cutoffTime ?? 0,
    totalMinted: publicSaleMinted,
    saleOngoing: hasOnGoingPublicSale,
    saleStarted: hasPublicSaleStarted,
    canMintPublicSale,
    saleEnded: hasPublicSaleEnded,
    isSoldOut: publicSaleSoldOut,
    formattedPrice: formattedPublicSalePrice ?? '',
    maxMintableLower: publicSale?.maxMintableLower ?? 0,
    maxSupply: publicMaxSupply,
  };

  const startTimestamp = getCurrentStartTimestamp({
    presaleDetails,
    publicSaleDetails,
    isAuthenticatedWithAPI,
  });

  return {
    presaleDetails,
    publicSaleDetails,
    hasOnGoingSale,
    bothSalesOngoing,
    startTimestamp,
    isOpenEdition,
    isFixedEdition,
    loading,
    rangeExpiryCheck,
    OEMinNotHit,
    publicSaleSchedule: publicSale,
    presaleSchedule: presale,
  };
};

/**
 * gets the current start timestamp between presale and public sale
 */
export const getCurrentStartTimestamp = ({
  presaleDetails,
  publicSaleDetails,
  isAuthenticatedWithAPI,
}: {
  presaleDetails: PresaleDetails;
  publicSaleDetails: PublicSaleDetails;
  isAuthenticatedWithAPI: boolean;
}) => {
  const hasPresale = !!presaleDetails;

  if (!publicSaleDetails.saleStarted) {
    //if not authenticated always show public sale start time
    if (!isAuthenticatedWithAPI) {
      return publicSaleDetails.startTime;
    }

    if (hasPresale && !presaleDetails.saleStarted) {
      //presale not started an no access show public sale start time
      if (!presaleDetails.hasAccess) return publicSaleDetails.startTime;
      //has access show presale start time
      return presaleDetails.startTime;
    }

    return publicSaleDetails.startTime;
  }

  return publicSaleDetails.startTime;
};

export const hasSaleStarted = ({ startTimestamp }: { startTimestamp: number }) => {
  const startTime = fromUnixTime(startTimestamp);
  const now = new Date();
  return isBefore(startTime, now);
};

export const isCurrentSaleOpenEdition = ({
  presaleMaxSupply,
  publicMaxSupply,
  hasPresaleAccess = false,
  hasOngoingPresale = false,
  isAuthenticatedWithAPI,
  publicSaleCutOff,
  minNotHit,
}: {
  presaleMaxSupply: number;
  publicMaxSupply: number;
  hasPresaleAccess?: boolean;
  hasOngoingPresale?: boolean;
  isAuthenticatedWithAPI: boolean;
  publicSaleCutOff: boolean;
  minNotHit: boolean;
}) => {
  //if open edition does not hit min on cutoff time, we treat as fixed
  const rangeExpiryCheck = publicSaleCutOff && minNotHit;
  if (rangeExpiryCheck) return false;

  const hasPresale = presaleMaxSupply > 0;

  const publicSaleOE = isOpenEdition(publicMaxSupply);
  const presaleOE = isOpenEdition(presaleMaxSupply);

  if (!isAuthenticatedWithAPI) {
    return publicSaleOE;
  }

  if (hasPresale && hasOngoingPresale && hasPresaleAccess) {
    return presaleOE;
  }

  return publicSaleOE;
};

export const isSaleFixedEdition = ({
  saleSchedule,
  publicSaleCutOff,
  minNotHit,
}: {
  saleSchedule: Maybe<MintSchedule>;
  publicSaleCutOff: boolean;
  minNotHit: boolean;
}) => {
  const publicSaleFixed =
    saleSchedule?.mintType === 'RangeEdition' &&
    saleSchedule.maxMintableLower === saleSchedule.maxMintableUpper;

  //if range open edition does not hit min on cutoff time, we treat as fixed
  const rangeExpiryCheck = publicSaleCutOff && minNotHit;

  if (rangeExpiryCheck) return true;

  return publicSaleFixed;
};
